import React, { useEffect, useRef, useState } from 'react'
import { Button, InputNumber, Tooltip } from 'antd'
import { DeleteOutlined, EditOutlined, FontSizeOutlined } from '@ant-design/icons'
import {
  COLOR_COTA,
  FUENTE_TEXTO,
  INTERLINEA_TEXTO,
  SALIDA_ANCHO,
  SALIDA_ALTO,
  TEXTO_DEFECTO,
  dibujarFondoCotizacion,
  dibujarFotoConCotas,
  paddingTexto,
  tamanoVisible,
} from '../utils/cotizacionImagenUtils'

const COLORES_TEXTO = ['#1f2328', COLOR_COTA, '#1677ff', '#16a34a', '#6b7280', '#ffffff']

const ESQUINAS = ['nw', 'ne', 'sw', 'se']
// Lado mínimo de una foto en px de la imagen final.
const MIN_LADO = 120
// Resolución máxima de la vista previa de cada foto (la exportación usa la original).
const MAX_PREVIEW = 1200
const PADDING_CONTENEDOR = 16

const clamp = (v, min, max) => Math.min(Math.max(v, min), max)

const FotoPreview = ({ foto }) => {
  const ref = useRef(null)
  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    const { w, h } = tamanoVisible(foto)
    const k = Math.min(1, MAX_PREVIEW / Math.max(w, h))
    canvas.width = Math.round(w * k)
    canvas.height = Math.round(h * k)
    const ctx = canvas.getContext('2d')
    ctx.scale(k, k)
    dibujarFotoConCotas(ctx, foto)
  }, [foto])
  return <canvas ref={ref} />
}

// Edición en el lugar: contentEditable no controlado; el texto se entrega al
// salir (blur, Esc o Ctrl/Cmd + Enter). Enter solo hace salto de línea.
const EditorTexto = ({ valor, onTerminar }) => {
  const ref = useRef(null)
  useEffect(() => {
    const el = ref.current
    el.innerText = valor
    el.focus()
    const rango = document.createRange()
    rango.selectNodeContents(el)
    const sel = window.getSelection()
    sel.removeAllRanges()
    sel.addRange(rango)
  }, [])
  return (
    <span
      ref={ref}
      className="cotz-composicion__editable"
      contentEditable
      suppressContentEditableWarning
      onBlur={() => onTerminar(ref.current.innerText.replace(/\n+$/, ''))}
      onKeyDown={(e) => {
        e.stopPropagation()
        if (e.key === 'Escape' || (e.key === 'Enter' && (e.metaKey || e.ctrlKey))) {
          e.preventDefault()
          ref.current.blur()
        }
      }}
    />
  )
}

/**
 * Vista previa editable de la imagen de cotización: la tabla queda fija y cada
 * foto (con sus cotas) se puede mover y redimensionar sin deformarla.
 * `capas` viene en px de la imagen final: [{ i, x, y, w, h }], donde `i` es el
 * índice en `fotos`; el orden del arreglo es el orden de dibujo (la última encima).
 */
/**
 * `textos`: textos libres encima de todo, en px de la imagen final
 * ([{ id, texto, x, y, tamano, color, negrita, fondo }]). `onTextosChange`
 * recibe un updater (prev => next), como un setState.
 */
const ComposicionCotizacion = ({ fotos, leyenda, capas, onChange, textos = [], onTextosChange }) => {
  const contenedorRef = useRef(null)
  const fondoRef = useRef(null)
  const escalaRef = useRef(0)
  const arrastreRef = useRef(null)
  const soltarRef = useRef(null)
  const [escala, setEscala] = useState(0)
  const [activa, setActiva] = useState(null)
  const [textoActivo, setTextoActivo] = useState(null)
  const [editando, setEditando] = useState(null)
  // Estilo del último texto tocado: los textos nuevos salen igual.
  const ultimoEstiloTextoRef = useRef(TEXTO_DEFECTO)

  useEffect(() => {
    const el = contenedorRef.current
    if (!el) return undefined
    const medir = () => {
      const k = Math.min(
        (el.clientWidth - PADDING_CONTENEDOR * 2) / SALIDA_ANCHO,
        (el.clientHeight - PADDING_CONTENEDOR * 2) / SALIDA_ALTO,
      )
      escalaRef.current = Math.max(k, 0)
      setEscala(escalaRef.current)
    }
    medir()
    const ro = new ResizeObserver(medir)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  useEffect(() => {
    const canvas = fondoRef.current
    if (!canvas) return
    canvas.width = SALIDA_ANCHO
    canvas.height = SALIDA_ALTO
    dibujarFondoCotizacion(canvas.getContext('2d'), leyenda)
  }, [leyenda])

  const mover = (e) => {
    const a = arrastreRef.current
    if (!a || !escalaRef.current) return
    const dx = (e.clientX - a.x0) / escalaRef.current
    const dy = (e.clientY - a.y0) / escalaRef.current
    const c = a.capa
    let nueva
    if (a.modo === 'mover') {
      nueva = {
        ...c,
        x: clamp(c.x + dx, 0, SALIDA_ANCHO - c.w),
        y: clamp(c.y + dy, 0, SALIDA_ALTO - c.h),
      }
    } else {
      // Se redimensiona desde una esquina dejando fija la opuesta y sin deformar.
      const sx = a.modo.includes('e') ? 1 : -1
      const sy = a.modo.includes('s') ? 1 : -1
      const aspecto = c.w / c.h
      const anclaX = sx > 0 ? c.x : c.x + c.w
      const anclaY = sy > 0 ? c.y : c.y + c.h
      const maxW = Math.min(
        sx > 0 ? SALIDA_ANCHO - anclaX : anclaX,
        (sy > 0 ? SALIDA_ALTO - anclaY : anclaY) * aspecto,
      )
      const w = clamp(Math.max(c.w + sx * dx, (c.h + sy * dy) * aspecto), MIN_LADO, maxW)
      const h = w / aspecto
      nueva = { ...c, w, h, x: sx > 0 ? anclaX : anclaX - w, y: sy > 0 ? anclaY : anclaY - h }
    }
    onChange([...a.base.slice(0, -1), nueva])
  }

  useEffect(() => () => soltarRef.current?.(), [])

  // Registra el arrastre en la ventana hasta soltar el puntero.
  const arrastrar = (alMover) => {
    const soltar = () => {
      arrastreRef.current = null
      soltarRef.current = null
      window.removeEventListener('pointermove', alMover)
      window.removeEventListener('pointerup', soltar)
    }
    soltarRef.current = soltar
    window.addEventListener('pointermove', alMover)
    window.addEventListener('pointerup', soltar)
  }

  const iniciar = (e, idx, modo) => {
    e.preventDefault()
    e.stopPropagation()
    const capa = capas[idx]
    // La foto que se toca pasa al frente.
    const base = [...capas.slice(0, idx), ...capas.slice(idx + 1), capa]
    onChange(base)
    setActiva(capa.i)
    setTextoActivo(null)
    arrastreRef.current = { modo, x0: e.clientX, y0: e.clientY, capa, base }
    arrastrar(mover)
  }

  // ---------- Textos ----------
  const actualizarTexto = (id, cambios) => {
    onTextosChange?.((prev) => prev.map((t) => (t.id === id ? { ...t, ...cambios } : t)))
    const { texto, x, y, id: _, ...estilo } = cambios
    if (Object.keys(estilo).length) {
      ultimoEstiloTextoRef.current = { ...ultimoEstiloTextoRef.current, ...estilo }
    }
  }

  const eliminarTexto = (id) => {
    onTextosChange?.((prev) => prev.filter((t) => t.id !== id))
    setTextoActivo(null)
    setEditando(null)
  }

  const agregarTexto = () => {
    const { texto, x, y, id, ...estilo } = { ...TEXTO_DEFECTO, ...ultimoEstiloTextoRef.current }
    const nuevo = {
      ...estilo,
      texto: TEXTO_DEFECTO.texto,
      id: `t-${Date.now()}-${Math.round(Math.random() * 1e6)}`,
      x: SALIDA_ANCHO / 2 - estilo.tamano * 2,
      y: SALIDA_ALTO / 2 - estilo.tamano / 2,
    }
    onTextosChange?.((prev) => [...prev, nuevo])
    setActiva(null)
    setTextoActivo(nuevo.id)
    setEditando(nuevo.id)
  }

  const terminarEdicion = (id, texto) => {
    setEditando(null)
    if (!texto.trim()) {
      eliminarTexto(id)
      return
    }
    onTextosChange?.((prev) => prev.map((t) => (t.id === id ? { ...t, texto } : t)))
  }

  const iniciarTexto = (e, t) => {
    e.preventDefault()
    e.stopPropagation()
    setActiva(null)
    setTextoActivo(t.id)
    ultimoEstiloTextoRef.current = { tamano: t.tamano, color: t.color, negrita: t.negrita, fondo: t.fondo }
    const x0 = e.clientX
    const y0 = e.clientY
    arrastreRef.current = { modo: 'texto' }
    arrastrar((ev) => {
      if (!escalaRef.current) return
      const x = clamp(t.x + (ev.clientX - x0) / escalaRef.current, 0, SALIDA_ANCHO - t.tamano)
      const y = clamp(t.y + (ev.clientY - y0) / escalaRef.current, 0, SALIDA_ALTO - t.tamano)
      onTextosChange?.((prev) => prev.map((o) => (o.id === t.id ? { ...o, x, y } : o)))
    })
  }

  // Supr borra el texto seleccionado; Esc lo deselecciona.
  useEffect(() => {
    if (!textoActivo || editando) return undefined
    const onKey = (e) => {
      const tag = e.target?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault()
        eliminarTexto(textoActivo)
      } else if (e.key === 'Escape') {
        setTextoActivo(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  const texto = textos.find((t) => t.id === textoActivo)

  return (
    <div
      className="cotz-composicion"
      ref={contenedorRef}
      onPointerDown={() => {
        setActiva(null)
        setTextoActivo(null)
      }}
    >
      <div className="cotz-composicion__herramientas" onPointerDown={(e) => e.stopPropagation()}>
        <Button size="small" icon={<FontSizeOutlined />} onClick={agregarTexto}>
          Agregar texto
        </Button>
        {texto && (
          <div className="cotz-composicion__estilo-texto">
            <Tooltip title="Tamaño (px en la imagen final)">
              <InputNumber
                size="small"
                min={12}
                max={300}
                step={4}
                value={texto.tamano}
                suffix="px"
                style={{ width: 92 }}
                onChange={(v) => v && actualizarTexto(texto.id, { tamano: v })}
              />
            </Tooltip>
            {COLORES_TEXTO.map((color) => (
              <button
                key={color}
                type="button"
                className={`cotz-composicion__color ${texto.color === color ? 'is-activo' : ''}`}
                style={{ background: color }}
                onClick={() => actualizarTexto(texto.id, { color })}
              />
            ))}
            <Tooltip title="Negrita">
              <Button
                size="small"
                type={texto.negrita ? 'primary' : 'default'}
                onClick={() => actualizarTexto(texto.id, { negrita: !texto.negrita })}
              >
                <b>B</b>
              </Button>
            </Tooltip>
            <Tooltip title="Fondo blanco detrás del texto">
              <Button
                size="small"
                type={texto.fondo ? 'primary' : 'default'}
                onClick={() => actualizarTexto(texto.id, { fondo: !texto.fondo })}
              >
                Fondo
              </Button>
            </Tooltip>
            <Tooltip title="Editar texto (doble clic)">
              <Button size="small" icon={<EditOutlined />} onClick={() => setEditando(texto.id)} />
            </Tooltip>
            <Tooltip title="Eliminar texto (Supr)">
              <Button size="small" danger icon={<DeleteOutlined />} onClick={() => eliminarTexto(texto.id)} />
            </Tooltip>
          </div>
        )}
      </div>

      <div
        className="cotz-composicion__lienzo"
        style={{ width: SALIDA_ANCHO * escala, height: SALIDA_ALTO * escala }}
      >
        <canvas ref={fondoRef} className="cotz-composicion__fondo" />
        {capas.map((c, idx) => (
          <div
            key={c.i}
            className={`cotz-composicion__capa ${activa === c.i ? 'is-activa' : ''}`}
            style={{ left: c.x * escala, top: c.y * escala, width: c.w * escala, height: c.h * escala }}
            onPointerDown={(e) => iniciar(e, idx, 'mover')}
          >
            <FotoPreview foto={fotos[c.i]} />
            {ESQUINAS.map((esquina) => (
              <span
                key={esquina}
                className={`cotz-composicion__asa is-${esquina}`}
                onPointerDown={(e) => iniciar(e, idx, esquina)}
              />
            ))}
          </div>
        ))}
        {textos.map((t) => {
          const pad = paddingTexto(t)
          const enEdicion = editando === t.id
          return (
            <div
              key={t.id}
              className={`cotz-composicion__texto ${textoActivo === t.id ? 'is-activa' : ''} ${enEdicion ? 'is-editando' : ''}`}
              style={{
                left: (t.x - pad) * escala,
                top: (t.y - pad) * escala,
                padding: pad * escala,
                fontSize: t.tamano * escala,
                lineHeight: INTERLINEA_TEXTO,
                fontFamily: FUENTE_TEXTO,
                fontWeight: t.negrita ? 700 : 500,
                color: t.color,
                background: t.fondo ? 'rgba(255, 255, 255, 0.9)' : 'transparent',
              }}
              onPointerDown={(e) => (enEdicion ? e.stopPropagation() : iniciarTexto(e, t))}
              onDoubleClick={() => setEditando(t.id)}
            >
              {enEdicion ? <EditorTexto valor={t.texto} onTerminar={(v) => terminarEdicion(t.id, v)} /> : t.texto}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default ComposicionCotizacion
